/*
 *  -----------------------------------------------------------------------
 *    <copyright file="schedule-versions-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';

import { SCHEDULE_VERSIONS_GRID_COLUMNS } from '@components/admin/schedule-versions-grid/schedule-versions-grid.data';
import {
    ScheduleVersionsGridProps,
    SVTScheduleVersionFields,
} from '@components/admin/schedule-versions-grid/schedule-versions-grid.interface';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { IActionItemV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import GridSubHeader from '@components/core/grid-header/grid-sub-header';
import { ISubHeaderItem } from '@components/core/grid-header/grid-sub-header/grid-sub-header.interface';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';

const ScheduleVersionsGrid = ({
    title,
    schedules,
    setCurrentSchedule,
    openAddSchedules,
    openDeleteVersions,
    showViewSubjectVisitTemplateDialog,
    canEdit,
    isSysAdmin,
}: ScheduleVersionsGridProps) => {
    const { t } = useTranslation();

    const getRowActions = (schedule: SVTScheduleVersionFields): IActionItemV2[] => [
        {
            label: 'admin:editVersion',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentSchedule(schedule);
                openAddSchedules(Constants.scheduleDialogTypes.editScheduleVersion);
            },
            enabled: canEdit || isSysAdmin,
        },
        {
            label: 'admin:viewVisitTemplate',
            icon: ['far', 'eye'],
            onClick: () => {
                setCurrentSchedule(schedule);
                showViewSubjectVisitTemplateDialog();
            },
            enabled: canEdit || isSysAdmin,
        },
    ];

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.expandMenu,
            customClassName: 'grid-header__item--always-visible',
            expandMenuItems: [
                {
                    label: 'admin:editScheduleDetails',
                    onClick: () => {
                        openAddSchedules(Constants.scheduleDialogTypes.editScheduleGroup);
                        setCurrentSchedule(schedules[0]);
                    },
                    icon: ['fal', 'pen-to-square'],
                    enabled: canEdit,
                },
                {
                    label: 'admin:addVersion',
                    onClick: () => {
                        openAddSchedules(Constants.scheduleDialogTypes.addScheduleVersion);
                        setCurrentSchedule(schedules[0]);
                    },
                    icon: ['fal', 'add'],
                    enabled: canEdit,
                },
                {
                    label: 'admin:deleteLatestVersion',
                    onClick: () => {
                        openDeleteVersions();
                    },
                    icon: ['fal', 'trash'],
                    enabled: canEdit,
                },
            ],
        },
    ];

    const getSubHeaderData = (schedule: SVTScheduleVersionFields): ISubHeaderItem[] =>
        schedule
            ? [
                  { label: `${t('admin:active')}: `, text: t(schedule.active ? 'common:yes' : 'common:no') },
                  { label: `${t('admin:treatmentArmsGridTitle')}: `, text: schedule._treatmentArms || t('ra:n/a') },
                  { label: `${t('admin:cohortsGridTitle')}: `, text: schedule._cohorts || t('ra:n/a') },
              ]
            : [];

    return (
        <ExpandableGridV2
            className="svt-grid-vertical-scroll"
            title={title}
            customKey="versions-grid"
            id="svtVersionsGrid"
            key="svtVersionsGrid"
            items={schedules}
            hideCheckbox
            hideExpand
            scrollable="scrollable"
            groupable
            reorderable
            resizable
            pageable={false}
            rowActions={getRowActions}
            extraMenuItems={extraMenuItems}
            subHeader={<GridSubHeader data={getSubHeaderData(schedules?.[0])} />}
            columns={SCHEDULE_VERSIONS_GRID_COLUMNS(t)}
        />
    );
};

export default ScheduleVersionsGrid;
